import { CONSTANTS } from "./constants";

/**
 * Word validation and prefix pruning.
 *
 * Two sets, because the blocklist is two-tier and the tiers behave differently
 * (this mirrors the app's existing `WordDisplayPolicy`):
 *
 *   - `hard` blocklist entries — slurs and explicit terms — are stripped at
 *     build time and are simply not in the dictionary. A player who fires one
 *     takes a miss, and the solver never sees it, so it cannot reach analytics.
 *   - `soft` entries — mild vulgarity — stay VALID, because the player typed
 *     them and refusing a real word they traced is the frustration §1.2 is
 *     built to avoid. They are excluded from `revealable`, so "best word on a
 *     cold board" and every other surfaced word skips them.
 *
 * That split is what §5.1 asks for: "a word the player can't score must not
 * appear in analytics or best-word reveals". A soft word the player *can*
 * score is a different case from a hard word they cannot.
 */
export class Dictionary {
  readonly version: string;
  private readonly words: Set<string>;
  private readonly prefixes: Set<string>;
  private readonly softBlocked: Set<string>;
  /**
   * The frequency-ranked subset an ordinary player plausibly knows — the app's
   * `en_answer_words.txt`. Everything in here is also in `words`; this is a
   * familiarity tier, not a second dictionary.
   *
   * It exists because "how many words does this board hold" turned out to be a
   * poor measure of whether a board is fun: boards passed the gates on 250
   * words while every high-scoring line was ATHEROMAS and DIPNOAN. Analytics and
   * gates measure against this set so a board has to be scoreable by someone who
   * only knows ordinary English.
   */
  private readonly common: Set<string>;
  /**
   * Frequency rank within the common pool — 0 is the most frequent word in the
   * language. The source list is ordered by frequency, so its line index IS the
   * rank; this map just makes it O(1) to ask "how obvious is this word?".
   */
  private readonly commonRanks: Map<string, number>;

  private constructor(version: string, words: Set<string>, softBlocked: Set<string>,
                      common: Set<string>) {
    this.version = version;
    this.words = words;
    this.softBlocked = softBlocked;
    this.common = common;
    this.commonRanks = new Map();
    // Prefix set rather than a trie: one Set lookup per DFS step, and at ~125k
    // words of length 4..10 it costs a few hundred thousand short strings —
    // cheap next to the solver's search, and far simpler to keep identical
    // across three languages.
    this.prefixes = new Set<string>();
    for (const w of words) {
      for (let i = 1; i < w.length; i += 1) this.prefixes.add(w.slice(0, i));
    }
  }

  /**
   * Build from newline-separated uppercase words. Anything outside
   * MIN_LEN..MAX_LEN or containing non A-Z is dropped — the solver's ceiling and
   * the validator's floor must agree or the two disagree about what exists.
   */
  static fromWordList(text: string, version: string, softBlocked: Iterable<string> = [],
                      commonWords: Iterable<string> = []): Dictionary {
    const words = new Set<string>();
    for (const raw of text.split("\n")) {
      const w = raw.trim().toUpperCase();
      if (w.length < CONSTANTS.MIN_LEN || w.length > CONSTANTS.MAX_LEN) continue;
      if (!/^[A-Z]+$/.test(w)) continue;
      words.add(w);
    }
    const soft = new Set<string>();
    for (const s of softBlocked) soft.add(s.trim().toUpperCase());

    // Only entries that survived into the dictionary count as common — a word
    // filtered out for length or by the blocklist is not "familiar", it is absent.
    const common = new Set<string>();
    const ranks = new Map<string, number>();
    let rank = 0;
    for (const c of commonWords) {
      const w = c.trim().toUpperCase();
      if (!words.has(w) || common.has(w)) continue;
      common.add(w);
      ranks.set(w, rank);
      rank += 1;
    }
    const dict = new Dictionary(version, words, soft, common);
    for (const [w, r] of ranks) dict.commonRanks.set(w, r);
    return dict;
  }

  /** Is this a fireable word? Soft-blocked words are valid and do score. */
  has(word: string): boolean {
    return this.words.has(word);
  }

  /** Could any word start this way? Drives DFS pruning. */
  hasPrefix(p: string): boolean {
    return this.prefixes.has(p);
  }

  /** May this word be shown to the player — reveals, analytics, share cards? */
  revealable(word: string): boolean {
    return this.words.has(word) && !this.softBlocked.has(word);
  }

  /** Would an ordinary player plausibly know this word? */
  isCommon(word: string): boolean {
    return this.common.has(word);
  }

  /**
   * How obvious a word is: 0 = the single most frequent word in English,
   * higher = rarer. `Infinity` for anything outside the common pool, so a
   * "most obvious first" sort naturally pushes obscure words to the back.
   */
  commonRank(word: string): number {
    return this.commonRanks.get(word) ?? Infinity;
  }

  get size(): number {
    return this.words.size;
  }

  get commonSize(): number {
    return this.common.size;
  }
}
